import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../utils/firebase';

const PreviewGame = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [gameData, setGameData] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Game structure for preview
  const gameStructure = [
    { type: 'title', title: 'Game Title' },
    { type: 'quarter1', title: 'Quarter 1 Categories' },
    { type: 'quarter2', title: 'Quarter 2 Categories' },
    { type: 'halftime', title: 'Halftime Category' },
    { type: 'quarter3', title: 'Quarter 3 Categories' },
    { type: 'quarter4', title: 'Quarter 4 Categories' },
    { type: 'final', title: 'Final Category' },
    { type: 'tiebreaker', title: 'Tiebreaker Category' },
  ];

  useEffect(() => {
    const loadGame = async () => {
      try {
        setLoading(true);
        
        if (!gameId) {
          setError('No game ID provided');
          return;
        }

        let game = null;
        let isDraft = false;

        // First try to load from published games collection
        try {
          const gameDoc = await getDoc(doc(db, 'games', gameId));
          if (gameDoc.exists()) {
            game = { id: gameDoc.id, ...gameDoc.data() };
            console.log('Loaded published game data:', game);
          }
        } catch (publishedError) {
          console.log('Game not found in published collection, trying drafts...');
        }

        // If not found in published games, try to load from user's drafts
        if (!game && user?.uid) {
          try {
            const draftDoc = await getDoc(doc(db, `users/${user.uid}/drafts`, gameId));
            if (draftDoc.exists()) {
              game = { id: draftDoc.id, ...draftDoc.data() };
              isDraft = true;
              console.log('Loaded draft game data:', game);
            }
          } catch (draftError) {
            console.error('Error loading draft:', draftError);
          }
        }
        
        if (!game) {
          setError('Game not found');
          return;
        }
        
        // Verify user owns this game (check both creatorId and ownerId)
        const isOwner = game.creatorId === user?.uid || game.ownerId === user?.uid;
        if (!isOwner) {
          setError('You can only preview your own games');
          return;
        }

        setGameData({ ...game, isDraft });
        setLoading(false);
      } catch (error) {
        console.error('Error loading game:', error);
        setError('Failed to load game');
        setLoading(false);
      }
    };

    if (user) {
      loadGame();
    }
  }, [gameId, user]);

  const getCurrentSlideContent = () => {
    if (!gameData) return null;

    const slide = gameStructure[currentSlide];
    console.log('Current slide:', slide);
    
    switch (slide.type) {
      case 'title':
        return {
          type: 'title',
          title: gameData.title,
          description: gameData.description,
          categories: gameData.categories || [],
          difficulty: gameData.difficulty || 'medium'
        };

      case 'quarter1':
        return {
          type: 'categories',
          quarter: 1,
          categories: gameData.quarter1?.map(q => q.category).filter(Boolean) || []
        };

      case 'quarter2':
        return {
          type: 'categories',
          quarter: 2,
          categories: gameData.quarter2?.map(q => q.category).filter(Boolean) || []
        };

      case 'halftime':
        return {
          type: 'halftime',
          quarter: 'halftime',
          category: gameData.halftime?.category
        };

      case 'quarter3':
        return {
          type: 'categories',
          quarter: 3,
          categories: gameData.quarter3?.map(q => q.category).filter(Boolean) || []
        };

      case 'quarter4':
        return {
          type: 'categories',
          quarter: 4,
          categories: gameData.quarter4?.map(q => q.category).filter(Boolean) || []
        };

      case 'final':
        return {
          type: 'final',
          quarter: 'final',
          category: gameData.final?.category
        };

      case 'tiebreaker':
        return {
          type: 'tiebreaker',
          quarter: 'tiebreaker',
          category: gameData.tieBreaker?.category
        };

      default:
        return null;
    }
  };

  const nextSlide = () => {
    if (currentSlide < gameStructure.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const renderSlide = (content) => {
    if (!content) return null;

    console.log('Rendering slide with content:', content);

    switch (content.type) {
      case 'title':
        return (
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">{content.title}</h1>
            {gameData.isDraft && (
              <div className="mb-4">
                <span className="bg-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  📄 Draft Preview
                </span>
              </div>
            )}
            {content.description && (
              <p className="text-xl text-gray-300 mb-6">{content.description}</p>
            )}
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              {content.categories.map((category, index) => (
                <span key={index} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                  {category}
                </span>
              ))}
            </div>
            <div className="text-gray-400">
              Difficulty: <span className="capitalize">{content.difficulty}</span>
            </div>
          </div>
        );

      case 'categories':
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Quarter {content.quarter} Categories
            </h2>
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
              {content.categories.map((category, index) => (
                <div key={index} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <h3 className="text-xl font-semibold text-white">{category}</h3>
                </div>
              ))}
            </div>
          </div>
        );

      case 'halftime':
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Halftime Category
            </h2>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-semibold text-white">{content.category || 'Category TBD'}</h3>
              </div>
            </div>
          </div>
        );

      case 'final':
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Final Category
            </h2>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-semibold text-white">{content.category || 'Category TBD'}</h3>
              </div>
            </div>
          </div>
        );

      case 'tiebreaker':
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Tiebreaker Category
            </h2>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <h3 className="text-xl font-semibold text-white">{content.category || 'Category TBD'}</h3>
              </div>
            </div>
          </div>
        );

      default:
        return <div className="text-white">Unknown slide type</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">{error}</div>
          <button
            onClick={() => navigate('/my-games')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to My Games
          </button>
        </div>
      </div>
    );
  }

  const currentContent = getCurrentSlideContent();

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/my-games')}
              className="text-gray-400 hover:text-white"
            >
              ← Back to My Games
            </button>
            <h1 className="text-xl font-semibold text-white">Preview Mode</h1>
          </div>
          
          <div className="text-center">
            <div className="text-sm text-gray-400 mb-1">Preview Mode — hosting tools are disabled</div>
            <div className="text-xs text-gray-500">Subscribe to host live</div>
          </div>

          <div className="text-right">
            <div className="text-sm text-gray-400">
              Slide {currentSlide + 1} of {gameStructure.length}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex space-x-1">
            {gameStructure.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`flex-1 h-2 rounded transition-colors ${
                  index === currentSlide 
                    ? 'bg-blue-600' 
                    : index < currentSlide 
                      ? 'bg-gray-600' 
                      : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="min-h-[600px] flex items-center justify-center">
          {currentContent && renderSlide(currentContent)}
        </div>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex space-x-4">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              currentSlide === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Previous
          </button>
          
          <button
            onClick={nextSlide}
            disabled={currentSlide === gameStructure.length - 1}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
              currentSlide === gameStructure.length - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Slide Navigation */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2">
        <div className="bg-gray-800 rounded-lg p-2 border border-gray-700">
          <div className="text-xs text-gray-400 mb-2 text-center">Slides</div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {gameStructure.map((slide, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`block w-full text-left px-2 py-1 rounded text-xs transition-colors ${
                  index === currentSlide
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {slide.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewGame;
